import moment from 'moment';

import {
    Schema, MapSchema, StringSchema,
    NumberSchema,
    required, length, range
} from 'persistent-models';
import FormController from 'react-mui-lib/form/controller';
import registry from 'react-mui-lib/registry';

import MomentSchema from '../locallib/moment-schema';
import FormDelegate from '../locallib/form-delegate';
import history from '../misc/history';
import { has_superaccess } from '../utils';


const schema = new MapSchema({
    franchise_id: StringSchema({
        validate: function () {
            this.setFlag('hidden', !has_superaccess());
            this.setFlag('submit', has_superaccess());
            this.callValidator(required('Поле должно быть заполнено'));
        }
    }),
    is_published: new Schema({
        initialValue: false
    }),
    plan: StringSchema({
        initialValue: null
    }),
    name: StringSchema({
        validate: function () {
            this.callValidator(required('Поле должно быть заполнено'));
            this.callValidator(length(0, 30, 'Название не должно быть длиннее 30 символов'));
        }
    }),
    description: StringSchema({
        validate: function () {
            this.callValidator(required('Поле должно быть заполнено'));
        }
    }),
    description_image: StringSchema({
        validate: function () {
            this.callValidator(required('Поле должно быть заполнено'));
        }
    }),
    location: StringSchema({
        validate: function () {
            this.callValidator(required('Поле должно быть заполнено'));
        }
    }),
    prize: StringSchema({
        validate: function () {
            const enable = this.forPath('game_type').getModelValue() !== 'TOUR';

            this.setFlag('hidden', !enable);
            this.setFlag('submit', enable);

            this.callValidator(required('Поле должно быть заполнено'));
        }
    }),
    start_date: new MomentSchema({
        initialValue: moment(),
        parser_message: 'Введите дату и время, например, 20.08.2017 12:00',
        validate: function () {
            this.callValidator(required('Поле должно быть заполнено'));
        }
    }),
    end_date: new MomentSchema({
        initialValue: moment(),
        parser_message: 'Введите дату и время, например, 20.08.2017 12:00',
        validate: function () {
            this.callValidator(required('Поле должно быть заполнено'));

            if (!has_superaccess()) {
                const plan = this.forPath('plan').getModelValue();
                const start = this.forPath('start_date').getModelValue();
                const end = this.getModelValue();
                if (plan === 'novice' && start && end && end.diff(start, 'seconds') > 24 * 3600) {
                    this.setValid('plan_range', false, 'Для тарифа Новичок игра не может длиться более 24 часов');
                } else if (plan === 'specialist' && start && end && end.diff(start, 'seconds') > 3 * 24 * 3600) {
                    this.setValid('plan_range', false, 'Для тарифа Специалист игра не может длиться более 3 дней');
                } else if (plan === 'specialist_plus' && start && end && end.diff(start, 'seconds') > 30 * 24 * 3600) {
                    this.setValid('plan_range', false, 'Для тарифа Специалист+ игра не может длиться более 30 дней');
                } else if (plan === 'profi' && start && end && end.diff(start, 'seconds') > 7 * 24 * 3600) {
                    this.setValid('plan_range', false, 'Для тарифа Профи игра не может длиться более 7 дней');
                } else if (plan === 'expert' && start && end && end.diff(start, 'seconds') > 30 * 24 * 3600) {
                    this.setValid('plan_range', false, 'Для тарифа Эксперт игра не может длиться более 30 дней');
                } else {
                    this.setValid('plan_range', true);
                }
            } else {
                this.setValid('plan_range', true);
            }
        }
    }),
    interim_message: StringSchema({
        validate: function () {
            const enable =
                this.forPath('round_mode').getModelValue() === 'SELECTABLE' &&
                this.forPath('game_type').getModelValue() !== 'TOUR';

            this.setFlag('hidden', !enable);
            this.setFlag('submit', enable);

            this.callValidator(required('Поле должно быть заполнено'));
        }
    }),
    finished_message: StringSchema({
        validate: function () {
            this.callValidator(required('Поле должно быть заполнено'));
        }
    }),
    winner_message: StringSchema({
        validate: function () {
            const enable = this.forPath('game_type').getModelValue() !== 'TOUR';

            this.setFlag('hidden', !enable);
            this.setFlag('submit', enable);

            this.callValidator(required('Поле должно быть заполнено'));
        }
    }),
    game_type: StringSchema({
        validate: function () {
            this.callValidator(required('Поле должно быть заполнено'));

            if (!has_superaccess()) {
                const plan = this.forPath('plan').getModelValue();
                const value = this.getModelValue();
                if ((plan === 'novice' || plan === 'specialist' || plan === 'specialist_plus' || plan === 'profi') && value === 'TOUR') {
                    this.setValid('plan_type', false, 'Для данного тарифа тип "Экскурсия" недоступен');
                } else if ((plan === 'profi' || plan === 'expert') && value === 'QUIZ') {
                    this.setValid('plan_type', false, 'Для данного тарифа тип "Квиз" недоступен');
                } else if ((plan === 'specialist' || plan === 'specialist_plus' || plan === 'expert') && value === 'GAME') {
                    this.setValid('plan_type', false, 'Для данного тарифа тип "Квест" недоступен');
                } else {
                    this.setValid('plan_type', true);
                }
            } else {
                this.setValid('plan_type', true);
            }
        }
    }),
    access_mode: StringSchema({
        validate: function () {
            this.callValidator(required('Поле должно быть заполнено'));

            if (!has_superaccess()) {
                const plan = this.forPath('plan').getModelValue();
                const value = this.getModelValue();
                if (plan === 'novice' && value !== 'PRIVATE') {
                    this.setValid('novice_access', false, 'Для тарифа Новичок доступ должен быть "Закрытый"');
                } else if (plan === 'specialist_plus' && value !== 'PUBLIC') {
                    this.setValid('novice_access', false, 'Для тарифа Специалист+ доступ должен быть "Открытый"');
                } else {
                    this.setValid('novice_access', true);
                }
            } else {
                this.setValid('novice_access', true);
            }
        }
    }),
    round_mode: StringSchema({
        validate: function () {
            this.callValidator(required('Поле должно быть заполнено'));
        }
    }),
    n_winners: NumberSchema({
        initialValue: 5,
        validate: function () {
            const is_selectable = this.forPath('round_mode').getModelValue() === 'SELECTABLE';

            this.setFlag('hidden', is_selectable);
            this.setFlag('submit', !is_selectable);

            this.callValidator(required('Поле должно быть заполнено'));
            this.callValidator(range(1, 10, 'Должно быть от 1 до 10 победителей'));
        }
    }),
    last_round_start_date: new MomentSchema({
        initialValue: moment(),
        parser_message: 'Введите дату и время, например, 20.08.2017 12:00',
        validate: function () {
            const is_selectable = this.forPath('round_mode').getModelValue() === 'SELECTABLE';

            this.setFlag('hidden', !is_selectable);
            this.setFlag('submit', is_selectable);

            this.callValidator(required('Поле должно быть заполнено'));
        }
    }),
    round_message: StringSchema({
        validate: function () {
            const enable =
                this.forPath('round_mode').getModelValue() === 'SELECTABLE' &&
                this.forPath('game_type').getModelValue() !== 'TOUR';

            this.setFlag('hidden', !enable);
            this.setFlag('submit', enable);

            this.callValidator(required('Поле должно быть заполнено'));
        }
    }),
    organizer_info: StringSchema({
        validate: function () {
            this.setFlag('hidden', true);
            this.setFlag('submit', false);
        }
    }),
});


class Delegate extends FormDelegate {
    didSubmit(json_response) {
        const c = this.controller;
        const game_id = c.state.entity_id || json_response.id;
        registry.get('game_grid_controller').needsRefresh();
        history.push(`/admin/game/${game_id}`);

        const rounds = registry.get('round_selector_controller');
        if (rounds.state.game_id !== game_id || rounds.state.rows.filter(r => r.is_published).length < 3) {
            window.dispatchEvent(new CustomEvent('game-tasks-hint'));
        }
    }
}


export default new FormController({
    schema,
    entity_name: 'game',
    state_prefix: 'game_form',
    Delegate
});
